import get from 'lodash/get.js';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter.js';
import customParseFormat from 'dayjs/plugin/customParseFormat.js';
import { vacationStatusList } from './constants.js';
import { formatDate } from './dateHelper.js';

dayjs.extend(isSameOrAfter);
dayjs.extend(customParseFormat);

export const filterOwnEvents = (events, id, show) => {
    if (!show) {
        const filtered = {
            daysOff: [],
        };
        for (let key in events) {
            if (key === 'daysOff') {
                for (let j in events.daysOff) {
                    filtered.daysOff[j] = events.daysOff[j].filter(({ UserId }) => id === UserId);
                }
                continue;
            }
            filtered[key] = [];
            for (let i = 0; i < events[key].length; i++) {
                if (events[key][i] && events[key][i].UserId !== id) {
                    continue;
                }
                filtered[key].push(events[key][i]);
            }
        }
        return filtered;
    }
    return events;
};

export const getUserNameLabel = (User) => `${User.lastName} ${User.firstName[0]}.`;

const getSickLeaveLabel = (date, cur) => {
    let label = '8h';
    if (date.isSame(cur.startDate, 'date')) {
        label = cur.isHalfOfFirstDay ? '4h' : label;
    }
    if (date.isSame(cur.endDate, 'date')) {
        label = cur.isHalfOfLastDay ? '4h' : label;
    }
    return label;
};

const sortTeamEvents = (a, b) => {
    const aStart = dayjs(get(a, 'startDate', a.date));
    const aEnd = dayjs(get(a, 'endDate', a.date));
    const bStart = dayjs(get(b, 'startDate', b.date));
    const bEnd = dayjs(get(b, 'endDate', b.date));

    const aDiff = aEnd.diff(aStart, 'day');
    const bDiff = bEnd.diff(bStart, 'day');
    if (aStart.isBefore(bStart)) {
        return -1;
    } else if (aStart.isAfter(bStart)) {
        return 1;
    }
    // sort by event duration on one day
    if (aDiff > bDiff) {
        return -1;
    }
    return 0;
};

function setEvent(obj, key, index, item) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
        obj[key][index] = item;
    } else {
        obj[key] = [];
        obj[key][index] = item;
    }
}

const MONDAY_DAY_VALUE = 1;

function formatVacation(obj, v, userId) {
    let i = 0;
    const duration = dayjs(v.endDate).diff(v.startDate, 'day');
    let date = dayjs(v.startDate);
    let index = 0;

    do {
        const key = formatDate(date);
        if (i === 0) {
            index = get(obj, key, []).length;
        }

        const isMonday = date.day() === MONDAY_DAY_VALUE;

        const item = {
            ...v,
            index,
            UserId: v.User.id,
            statusName: userId === v.User.id ? vacationStatusList[v.status].statusName : 'team',
            label: userId === v.User.id ? vacationStatusList[v.status].valueForCalendar : getUserNameLabel(v.User),
            isFirstDay: i === 0,
            isMonday,
            isLastDay: i === duration,
        };
        setEvent(obj, key, index, item);
        i++;
        date = date.add(1, 'day');
    } while (i <= duration);
}

function getSickLeaveLabelForUser(v, userId, isNotFirstDay, isMonday, label) {
    if (userId === v.UserId) {
        return label;
    }
    if (isNotFirstDay) {
        return isMonday ? `${getUserNameLabel(v.User)}, ${label}` : label;
    }
    return `${getUserNameLabel(v.User)}, ${label}`;
}

function formatSickLeave(obj, v, userId) {
    let i = 0;
    const duration = dayjs(v.endDate).diff(v.startDate, 'day');
    let date = dayjs(v.startDate);
    let index = 0;

    do {
        const key = formatDate(date);
        const isMonday = date.day() === MONDAY_DAY_VALUE;
        const label = getSickLeaveLabel(date, v);

        if (i === 0) {
            index = get(obj, key, []).length;
        }

        const item = {
            ...v,
            index,
            isFirstDay: i === 0,
            isLastDay: i === duration,
            statusName: userId !== v.User.id ? 'team' : dayjs().isSameOrAfter(v.endDate, 'date') ? 'ended' : 'approved',
            label: getSickLeaveLabelForUser(v, userId, i, isMonday, label),
            isMonday,
        };
        setEvent(obj, key, index, item);
        i++;
        date = date.add(1, 'day');
    } while (i <= duration);
}

function formatWorking(obj, v, userId) {
    const workKey = formatDate(v.date);
    const workIndex = get(obj, workKey, []).length;

    const item = {
        ...v,
        index: workIndex,
        isFirstDay: true,
        isLastDay: true,
        label: userId === v.UserId ? formatDate(v.date) : `${getUserNameLabel(v.User)}, ${formatDate(v.date)}`,
        statusName: userId !== v.UserId ? 'team' : dayjs().isSameOrAfter(v.date, 'date') ? 'ended' : 'approved',
        isSwap: true,
        swapDirection: 'working',
    };
    setEvent(obj, workKey, workIndex, item);

    const offKey = formatDate(v.insteadDate);
    const offIndex = get(obj, offKey, []).length;

    const offItem = {
        ...v,
        index: offIndex,
        isFirstDay: true,
        isLastDay: true,
        label:
            userId === v.UserId
                ? `Works ${formatDate(v.date)} instead`
                : `${getUserNameLabel(v.User)}, works ${formatDate(v.date)} instead`,
        statusName: userId !== v.UserId ? 'team' : 'swap-off',
        type: 'swapOff',
        isSwap: true,
        swapDirection: 'off',
    };
    setEvent(obj, offKey, offIndex, offItem);
}

function formatWorkingTime(obj, v, userId) {
    const key = formatDate(v.date);
    const index = get(obj, key, []).length;
    const label = v.hours + 'h, ' + vacationStatusList[v.status].valueForCalendar;

    const item = {
        ...v,
        index,
        isFirstDay: true,
        isLastDay: true,
        statusName: userId !== v.UserId ? 'team' : vacationStatusList[v.status].statusName,
        label: userId === v.UserId ? label : `${getUserNameLabel(v.User)}, ${label}`,
    };
    setEvent(obj, key, index, item);
}

export function formatTeamEvents(teamEvents, userId) {
    const { vacations, overtimes, sickLeaves, workingDays } = teamEvents;

    const sortedList = [
        ...vacations.map((v) => ({ ...v, type: 'vacation' })),
        ...overtimes.map((v) => ({ ...v, type: 'workingTime' })),
        ...sickLeaves.map((v) => ({ ...v, type: 'sickLeave' })),
        ...workingDays.map((v) => ({ ...v, type: 'working' })),
    ].sort(sortTeamEvents);

    let a = sortedList.reduce((obj, v) => {
        switch (v.type) {
            case 'vacation': {
                formatVacation(obj, v, userId);
                return obj;
            }
            case 'sickLeave': {
                formatSickLeave(obj, v, userId);
                return obj;
            }
            case 'working': {
                formatWorking(obj, v, userId);
                return obj;
            }
            case 'workingTime': {
                formatWorkingTime(obj, v, userId);
                return obj;
            }
            default:
                return obj;
        }
    }, {});

    return { ...a, daysOff: {} };
}
